import { Marked, type Token, type Tokens, type TokensList } from 'marked'
import { parseTimecodeToSeconds, toStoredTimecodeSeconds } from '../meeting-notes-document.js'
import {
  MEETING_NOTES_HEADING_LEVELS,
  MEETING_NOTES_MARK_ORDER,
  MEETING_NOTES_ORDERED_LIST_ATTRS,
  MEETING_NOTES_TABLE_CELL_ATTRS,
  type MeetingNotesDoc,
  type MeetingNotesMark,
  type MeetingNotesNode,
} from './doc-model.js'

/**
 * Markdown → the ProseMirror document a meeting note is made of.
 *
 * The producer is a language model, not a person filling in a form, so this
 * converter NEVER throws. That is the one place it parts company with
 * `blog/markdown-to-doc.ts`, whose caller is an MCP client that would rather be
 * told its picture is unsupported than have it vanish: here, refusing a document
 * would fail a whole 整理 the user has already paid credits for, over a line
 * nobody can go back and edit. Everything the schema cannot hold therefore
 * degrades to text, exactly as the hand-written renderer this replaces did.
 *
 * Tokens come from `marked`'s LEXER, never its HTML output. Going through HTML
 * and back would hand the schema a document to re-parse, and a schema drops what
 * it does not know about — the silent loss this module exists to make
 * impossible.
 */

/**
 * Private parser instance. A module-level `marked.use()` would mutate a parser
 * every other consumer in the process shares; a `Marked` instance holds nothing
 * but its defaults and builds a fresh `Lexer` per call.
 */
const notesMarked = new Marked({ gfm: true })

export interface MeetingNotesMarkdownOptions {
  /**
   * Move a timecode the model wrote to a real cue boundary. The seek target is
   * the result to the millisecond; the visible label is the whole second it
   * falls in, derived from that same value so the two can never disagree — if
   * the label kept the model's original time, converting the note's Markdown
   * back would undo the snap.
   *
   * Must be idempotent under flooring: `snap(floor(snap(x))) === snap(x)`,
   * because Markdown carries only the floored label, and converting it back
   * has to land where it started.
   */
  snapSeconds?: (seconds: number) => number
}

function markRank(type: string): number {
  return (MEETING_NOTES_MARK_ORDER as readonly string[]).indexOf(type)
}

function withMark(marks: MeetingNotesMark[], type: string): MeetingNotesMark[] {
  if (marks.some((mark) => mark.type === type)) return marks
  return [...marks, { type }].sort((left, right) => markRank(left.type) - markRank(right.type))
}

function sameMarks(left: MeetingNotesMark[] | undefined, right: MeetingNotesMark[]): boolean {
  return JSON.stringify(left ?? []) === JSON.stringify(right)
}

function withMarks(node: MeetingNotesNode, marks: MeetingNotesMark[]): MeetingNotesNode {
  return marks.length > 0 ? { ...node, marks } : node
}

/**
 * Appends a text run, merging into the previous one when the marks match.
 *
 * ProseMirror merges adjacent text nodes carrying the same mark set on the way
 * in, so a parser that emitted `甲`, `*`, `乙` as three runs (which is what an
 * escaped `\*` produces) would build a document that is `toJSON`-unequal to the
 * one the editor stores for the same note.
 *
 * A SOFT line break inside a paragraph collapses to one space, which is what
 * the hand-written renderer did (it joined a paragraph's lines with a space) and
 * what a browser shows for the same HTML anyway. A HARD break is a `hardBreak`
 * node and never reaches here.
 */
function appendText(nodes: MeetingNotesNode[], raw: string, marks: MeetingNotesMark[]): void {
  const text = raw.replace(/[ \t]*\r?\n[ \t]*/g, ' ')
  if (text === '') return
  const previous = nodes.at(-1)
  if (previous?.type === 'text' && sameMarks(previous.marks, marks)) {
    previous.text = `${previous.text ?? ''}${text}`
    return
  }
  nodes.push(withMarks({ type: 'text', text }, marks))
}

function timecodeNode(
  text: string,
  marks: MeetingNotesMark[],
  options: MeetingNotesMarkdownOptions
): MeetingNotesNode | null {
  const parsed = parseTimecodeToSeconds(text)
  if (parsed === null) return null
  const seconds = toStoredTimecodeSeconds(options.snapSeconds?.(parsed) ?? parsed)
  return withMarks({ type: 'timecode', attrs: { seconds } }, marks)
}

function collectInline(
  tokens: Token[] | undefined,
  marks: MeetingNotesMark[],
  into: MeetingNotesNode[],
  options: MeetingNotesMarkdownOptions
): void {
  for (const token of tokens ?? []) {
    switch (token.type) {
      case 'text':
      case 'escape': {
        const nested = (token as Tokens.Text).tokens
        if (nested && nested.length > 0) collectInline(nested, marks, into, options)
        else appendText(into, token.text, marks)
        break
      }
      case 'strong':
        collectInline(token.tokens, withMark(marks, 'bold'), into, options)
        break
      case 'em':
        collectInline(token.tokens, withMark(marks, 'italic'), into, options)
        break
      // No strike in the schema, so `~~甲~~` keeps its words and loses only the
      // styling — the same trade every unsupported construct below makes.
      case 'del':
        collectInline(token.tokens, marks, into, options)
        break
      case 'codespan': {
        // Backticks are the note dialect's TIMECODE syntax (D6). Anything else
        // inside them is prose whose backticks are eaten, which is what the
        // hand-written renderer did and what the schema (no `code` mark) can
        // hold.
        const timecode = timecodeNode(token.text, marks, options)
        if (timecode) into.push(timecode)
        else appendText(into, token.text, marks)
        break
      }
      // A link keeps its label: the schema has no `link` mark, and a URL in the
      // middle of a sentence is noise in a meeting note.
      case 'link':
        collectInline(token.tokens, marks, into, options)
        break
      case 'br':
        into.push({ type: 'hardBreak' })
        break
      // `raw`, not `text`: an image's alt is routinely empty, and a node that
      // degrades to nothing at all is indistinguishable from data loss.
      case 'image':
      case 'html':
        appendText(into, token.raw, marks)
        break
      default:
        appendText(into, (token as Tokens.Generic).raw ?? '', marks)
    }
  }
}

function inlineNodes(
  tokens: Token[] | undefined,
  options: MeetingNotesMarkdownOptions
): MeetingNotesNode[] {
  const nodes: MeetingNotesNode[] = []
  collectInline(tokens, [], nodes, options)
  return nodes
}

/** A node with a `content` key only when there is content — the shape `toJSON` writes. */
function blockNode(type: string, content: MeetingNotesNode[]): MeetingNotesNode {
  return content.length > 0 ? { type, content } : { type }
}

function paragraphOf(text: string): MeetingNotesNode {
  const content: MeetingNotesNode[] = []
  appendText(content, text, [])
  return blockNode('paragraph', content)
}

function headingNode(
  token: Tokens.Heading,
  options: MeetingNotesMarkdownOptions
): MeetingNotesNode {
  const content = inlineNodes(token.tokens, options)
  if (!(MEETING_NOTES_HEADING_LEVELS as readonly number[]).includes(token.depth)) {
    // `#` is the page title's level and `####` and below have no node. The
    // WORDS still have to arrive, so the marker is dropped and the line becomes
    // a paragraph — the same thing the editor does with a pasted `<h1>`.
    return blockNode('paragraph', content)
  }
  return { type: 'heading', attrs: { level: token.depth }, content }
}

/**
 * A fenced or indented code block, flattened into one paragraph whose line
 * breaks are `hardBreak`s.
 *
 * There is no code node in the schema. Dropping the block would delete whatever
 * the model put in it; joining its lines with spaces would run them together.
 * Hard breaks are the one shape that keeps both the text and its lines and
 * survives the trip back out (`doc-to-markdown` writes them as CommonMark hard
 * line breaks).
 */
function codeBlockNode(token: Tokens.Code): MeetingNotesNode {
  const content: MeetingNotesNode[] = []
  for (const [index, line] of token.text.split('\n').entries()) {
    if (index > 0) content.push({ type: 'hardBreak' })
    appendText(content, line, [])
  }
  return blockNode('paragraph', content)
}

function tableNode(token: Tokens.Table, options: MeetingNotesMarkdownOptions): MeetingNotesNode {
  const cell = (source: Tokens.TableCell, type: string): MeetingNotesNode => ({
    type,
    attrs: { ...MEETING_NOTES_TABLE_CELL_ATTRS },
    // `paragraph+`: an empty cell is an empty paragraph, never an empty cell.
    content: [blockNode('paragraph', inlineNodes(source.tokens, options))],
  })
  const rows: MeetingNotesNode[] = [
    { type: 'tableRow', content: token.header.map((header) => cell(header, 'tableHeader')) },
  ]
  for (const row of token.rows) {
    rows.push({ type: 'tableRow', content: row.map((column) => cell(column, 'tableCell')) })
  }
  return { type: 'table', content: rows }
}

/**
 * A heading marker that leads a LIST ITEM is noise, not structure.
 *
 * By the Markdown spec `- ## 內容` is a list item holding a heading, and the
 * schema would even accept one — but a template is user-authored markdown and
 * people paste whole prompts whose example skeletons look like
 * `* ## **[填寫此處]**`. The model copies that shape faithfully, and the reader
 * saw「## 醫美診所負責人」printed verbatim in their notes. Keep the words, drop
 * the marker; this is the node-level form of the old renderer's
 * `stripLeadingHeadingMarker`.
 */
function demoteHeading(node: MeetingNotesNode): MeetingNotesNode {
  if (node.type !== 'heading') return node
  return node.content === undefined
    ? { type: 'paragraph' }
    : { type: 'paragraph', content: node.content }
}

function listNode(token: Tokens.List, options: MeetingNotesMarkdownOptions): MeetingNotesNode {
  const items: MeetingNotesNode[] = []
  for (const item of token.items) {
    const blocks = blockNodes(item.tokens, options).map(demoteHeading)
    // `listItem` is `paragraph block*`, so an item that opens with a nested
    // list needs a paragraph to hang it off — which is also what ProseMirror
    // inserts when it parses the same HTML.
    if (blocks[0]?.type !== 'paragraph') blocks.unshift({ type: 'paragraph' })
    items.push({ type: 'listItem', content: blocks })
  }
  if (!token.ordered) return { type: 'bulletList', content: items }
  return {
    type: 'orderedList',
    attrs: { ...MEETING_NOTES_ORDERED_LIST_ATTRS },
    content: items,
  }
}

function blockNodes(tokens: Token[], options: MeetingNotesMarkdownOptions): MeetingNotesNode[] {
  const nodes: MeetingNotesNode[] = []
  for (const token of tokens) {
    switch (token.type) {
      case 'space':
        break
      case 'heading':
        nodes.push(headingNode(token as Tokens.Heading, options))
        break
      case 'paragraph':
      case 'text':
        nodes.push(blockNode('paragraph', inlineNodes(token.tokens, options)))
        break
      case 'code':
        nodes.push(codeBlockNode(token as Tokens.Code))
        break
      case 'blockquote': {
        // `blockquote` is `block+`; an empty one is a document the editor
        // cannot open, so it gets the empty paragraph ProseMirror would.
        const quoted = blockNodes((token as Tokens.Blockquote).tokens, options)
        nodes.push({
          type: 'blockquote',
          content: quoted.length > 0 ? quoted : [{ type: 'paragraph' }],
        })
        break
      }
      case 'list':
        nodes.push(listNode(token as Tokens.List, options))
        break
      case 'table':
        nodes.push(tableNode(token as Tokens.Table, options))
        break
      // No horizontal rule, no raw HTML and no link-reference definition in the
      // schema. Each keeps its source text as a paragraph — the reader sees
      // what the model wrote instead of a hole where it used to be.
      case 'hr':
      case 'html':
      default: {
        const text = ((token as Tokens.Generic).raw ?? '').trim()
        if (text !== '') nodes.push(paragraphOf(text))
      }
    }
  }
  return nodes
}

export function meetingNotesMarkdownToDoc(
  markdown: string,
  options: MeetingNotesMarkdownOptions = {}
): MeetingNotesDoc {
  const source = markdown.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n')
  const tokens: TokensList = notesMarked.lexer(source)
  const content = blockNodes(tokens, options)
  // `doc` is `block+`; an empty note is one empty paragraph, which is also what
  // the editor holds for a blank document.
  return { type: 'doc', content: content.length > 0 ? content : [{ type: 'paragraph' }] }
}
